import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PLANS = [
    {
        id: 'starter',
        name: 'Starter',
        tag: null,
        monthlyPrice: 0,
        yearlyPrice: 0,
        period: 'forever free',
        color: '#27ae60',
        quota: '1 report / week',
        features: [
            '1 free product report each week',
            'Full ROI & profit margin breakdown',
            'Retailer sourcing comparison',
            'Direct Amazon purchase links',
            'Professional PDF download',
        ],
        cta: 'Get Started Free',
        popular: false,
        free: true,
    },
    {
        id: 'pro',
        name: 'Pro',
        tag: null,
        monthlyPrice: 19,
        yearlyPrice: 190,
        period: '/month',
        color: '#e94560',
        quota: '1 report / day',
        features: [
            '1 report every day (30/month)',
            'All report features included',
            'Priority report generation',
            'Cancel anytime',
            'Billing history & invoices',
        ],
        cta: 'Start Pro',
        popular: true,
    },
    {
        id: 'premium',
        name: 'Premium',
        tag: null,
        monthlyPrice: 39,
        yearlyPrice: 390,
        period: '/month',
        color: '#0f3460',
        quota: 'Up to 5 reports / day',
        features: [
            'Up to 5 reports per day (150/month)',
            'All report features included',
            'Priority report generation',
            'Early access to new categories',
            'Cancel anytime',
        ],
        cta: 'Start Premium',
        popular: false,
    },
    {
        id: 'elite',
        name: 'Elite',
        tag: 'Best Value',
        monthlyPrice: 79,
        yearlyPrice: 790,
        period: '/month',
        color: '#7c3aed',
        quota: 'Unlimited reports',
        features: [
            'Unlimited daily reports',
            'API access',
            'Supplier contacts included',
            'Priority support',
            'Early access to all features',
        ],
        cta: 'Start Elite',
        popular: false,
    },
];

const GUARANTEES = [
    { icon: 'fa-ban', text: 'Cancel anytime' },
    { icon: 'fa-calendar-check', text: 'New reports every day' },
    { icon: 'fa-shield-alt', text: '30-day money-back guarantee' },
    { icon: 'fa-lock', text: 'Secure Stripe checkout' },
];

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [billing, setBilling] = useState('monthly');

    const handleSelect = (plan) => {
        if (plan.free) {
            if (!user) navigate('/register');
            else navigate('/dashboard');
            return;
        }
        if (!user) {
            navigate(`/register?plan=${plan.id}&billing=${billing}`);
        } else {
            navigate(`/checkout-options?plan=${plan.id}&billing=${billing}`);
        }
    };

    const displayPrice = (plan) => {
        if (plan.free) return 'Free';
        if (billing === 'yearly') return `$${plan.yearlyPrice / 12 % 1 === 0 ? plan.yearlyPrice / 12 : (plan.yearlyPrice / 12).toFixed(2)}`;
        return `$${plan.monthlyPrice}`;
    };

    const savingsLabel = (plan) => {
        if (plan.free) return null;
        const monthly = plan.monthlyPrice * 12;
        const yearly  = plan.yearlyPrice;
        const saved   = monthly - yearly;
        return `Save $${saved}/yr`;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero */}
            <div className="py-16 px-4 text-center" style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)' }}>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                    style={{ background: 'rgba(233,69,96,0.2)', border: '1px solid rgba(233,69,96,0.4)', color: '#ff9fba' }}>
                    Pricing
                </span>
                <h1 className="text-4xl font-extrabold text-white mb-3">
                    Find profitable Amazon products<br className="hidden sm:block" /> before your competitors do
                </h1>
                <p className="text-gray-300 max-w-xl mx-auto mb-8">
                    Receive daily reports of products with low competition and strong profit potential. Start free, upgrade anytime.
                </p>

                {/* Billing toggle */}
                <div className="inline-flex items-center rounded-full p-1 gap-1" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <button
                        onClick={() => setBilling('monthly')}
                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-white text-gray-900' : 'text-gray-300 hover:text-white'}`}>
                        Monthly
                    </button>
                    <button
                        onClick={() => setBilling('yearly')}
                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${billing === 'yearly' ? 'bg-white text-gray-900' : 'text-gray-300 hover:text-white'}`}>
                        Annual
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: '#e94560', color: 'white' }}>Save 17%</span>
                    </button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-16">
                {/* Plan cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
                    {PLANS.map(plan => (
                        <div key={plan.id}
                            className={`bg-white rounded-2xl overflow-hidden shadow-lg border-2 transition-all hover:-translate-y-1 flex flex-col ${plan.popular ? 'shadow-xl' : 'border-gray-100 hover:border-gray-200'}`}
                            style={{ borderColor: plan.popular ? '#e94560' : undefined }}>

                            {plan.popular && (
                                <div className="py-2 text-center text-xs font-bold uppercase tracking-wider text-white" style={{ background: '#e94560' }}>
                                    Most Popular
                                </div>
                            )}
                            {plan.tag && !plan.popular && (
                                <div className="py-2 text-center text-xs font-bold uppercase tracking-wider text-white" style={{ background: '#7c3aed' }}>
                                    {plan.tag}
                                </div>
                            )}

                            {/* Card header */}
                            <div className="p-6" style={{ background: '#1a1a2e' }}>
                                <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                                <div className="text-4xl font-black text-white mt-3">
                                    {displayPrice(plan)}
                                    {!plan.free && <span className="text-base font-normal text-gray-400">/mo</span>}
                                </div>
                                {!plan.free && (
                                    <div className="text-gray-400 text-xs mt-1">
                                        {billing === 'yearly'
                                            ? <span>Billed <strong className="text-white">${plan.yearlyPrice}/yr</strong> &nbsp;·&nbsp; <span style={{ color: '#2ed573' }}>{savingsLabel(plan)}</span></span>
                                            : 'Billed monthly'}
                                    </div>
                                )}
                                <div className="mt-2 text-xs font-semibold px-2 py-0.5 rounded inline-block"
                                    style={{ background: 'rgba(233,69,96,0.15)', color: '#ff9fba' }}>
                                    {plan.quota}
                                </div>
                            </div>

                            {/* Features */}
                            <div className="p-6 flex flex-col flex-1">
                                <ul className="space-y-3 mb-6 flex-1">
                                    {plan.features.map(f => (
                                        <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                                            <i className="fas fa-check-circle mt-0.5 flex-shrink-0" style={{ color: '#27ae60' }}></i>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => handleSelect(plan)}
                                    className="w-full py-3.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
                                    style={{ background: plan.popular ? '#e94560' : plan.free ? '#27ae60' : plan.id === 'elite' ? '#7c3aed' : '#1a1a2e' }}>
                                    {plan.cta}
                                </button>
                                {!user && !plan.free && (
                                    <p className="text-center text-xs text-gray-400 mt-2">
                                        <Link to="/register" style={{ color: '#e94560' }}>Sign up</Link> to purchase
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Guarantee strip */}
                <div className="rounded-2xl p-6 mb-14 grid grid-cols-2 md:grid-cols-4 gap-4" style={{ background: '#1a1a2e' }}>
                    {GUARANTEES.map(g => (
                        <div key={g.text} className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(233,69,96,0.2)' }}>
                                <i className={`fas ${g.icon} text-sm`} style={{ color: '#e94560' }}></i>
                            </div>
                            <span className="text-sm font-semibold text-white">{g.text}</span>
                        </div>
                    ))}
                </div>

                {/* Comparison table */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
                    <div className="p-6 border-b border-gray-100">
                        <h3 className="text-xl font-bold text-gray-900">Compare Plans</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ background: '#1a1a2e' }}>
                                    <th className="text-left px-6 py-4 text-gray-300 font-semibold">Feature</th>
                                    <th className="px-4 py-4 text-center text-gray-300 font-semibold">Starter</th>
                                    <th className="px-4 py-4 text-center text-white font-semibold" style={{ background: '#e94560' }}>Pro</th>
                                    <th className="px-4 py-4 text-center text-gray-300 font-semibold">Premium</th>
                                    <th className="px-4 py-4 text-center text-gray-300 font-semibold">Elite</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['Daily Reports',           '1/week',  '1/day',   '5/day',   'Unlimited'],
                                    ['PDF Download',            true,       true,       true,       true],
                                    ['ROI Analysis',            true,       true,       true,       true],
                                    ['Retailer Comparison',     true,       true,       true,       true],
                                    ['Price History Charts',    true,       true,       true,       true],
                                    ['Priority Processing',     false,      true,       true,       true],
                                    ['Early Access',            false,      false,      true,       true],
                                    ['API Access',              false,      false,      false,      true],
                                    ['Supplier Contacts',       false,      false,      false,      true],
                                    ['Priority Support',        false,      false,      false,      true],
                                ].map(([feature, starter, pro, premium, elite], i) => (
                                    <tr key={feature} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-3.5 font-medium text-gray-800">{feature}</td>
                                        {[starter, pro, premium, elite].map((val, j) => (
                                            <td key={j} className="px-4 py-3.5 text-center">
                                                {typeof val === 'boolean' ? (
                                                    <i className={`fas ${val ? 'fa-check-circle' : 'fa-times-circle'}`}
                                                        style={{ color: val ? '#27ae60' : '#e74c3c' }}></i>
                                                ) : (
                                                    <span className="font-medium" style={{ color: j === 1 ? '#e94560' : '#374151' }}>{val}</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <p className="text-center text-gray-400 text-sm">
                    All plans include secure Stripe payment processing. Subscriptions can be canceled anytime from your dashboard.
                </p>
            </div>
        </div>
    );
}
